import React, { useState, useEffect } from 'react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const DateFilterExportTest = () => {
  const { 
    fetchAttendance, 
    exportToPDF, 
    exportToCSV,
    attendanceList,
    pagination,
    isLoading 
  } = useAttendance();
  
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runDateFilterTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: July 2025 Data (Full Month)',
        test: async () => {
          try {
            const startDate = '2025-07-01';
            const endDate = '2025-07-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            return {
              passed: data.length > 0,
              message: `July 2025: ${data.length} records found`,
              data: { 
                recordCount: data.length,
                startDate,
                endDate,
                sampleDates: data.slice(0, 5).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 2: August 2025 Data (Full Month)',
        test: async () => {
          try {
            const startDate = '2025-08-01';
            const endDate = '2025-08-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            return {
              passed: data.length > 0,
              message: `August 2025: ${data.length} records found`,
              data: { 
                recordCount: data.length,
                startDate,
                endDate,
                sampleDates: data.slice(0, 5).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 3: September 2025 Data (Full Month)',
        test: async () => {
          try {
            const startDate = '2025-09-01';
            const endDate = '2025-09-30';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            return {
              passed: data.length > 0,
              message: `September 2025: ${data.length} records found`,
              data: { 
                recordCount: data.length,
                startDate,
                endDate,
                sampleDates: data.slice(0, 5).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: PDF Export Test (July 2025)',
        test: async () => {
          try {
            const startDate = '2025-07-01';
            const endDate = '2025-07-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to export',
                data: { recordCount: 0 }
              };
            }
            
            // Test PDF export
            const startTime = Date.now();
            await exportToPDF(data, `test_july_2025_${Date.now()}.pdf`);
            const endTime = Date.now();
            
            return {
              passed: true,
              message: `PDF export successful: ${data.length} records in ${endTime - startTime}ms`,
              data: { 
                recordCount: data.length,
                exportTime: endTime - startTime,
                startDate,
                endDate
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `PDF export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 5: CSV Export Test (August 2025)',
        test: async () => {
          try {
            const startDate = '2025-08-01';
            const endDate = '2025-08-31';
            
            const result = await fetchAttendance({
              startDate,
              endDate,
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to export',
                data: { recordCount: 0 }
              };
            }
            
            // Test CSV export
            const startTime = Date.now();
            await exportToCSV(data, `test_august_2025_${Date.now()}.csv`);
            const endTime = Date.now();
            
            return {
              passed: true,
              message: `CSV export successful: ${data.length} records in ${endTime - startTime}ms`,
              data: { 
                recordCount: data.length,
                exportTime: endTime - startTime,
                startDate,
                endDate
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `CSV export failed: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} date filter tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">📅 Date Filter & Export Test Suite</h2>
      
      <div className="mb-6">
        <button
          onClick={runDateFilterTests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run Date Filter Tests'}
        </button>
      </div>

      {/* Current Data Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Current Data</h3>
          <p className="text-sm text-gray-600">
            Records: {attendanceList.length}
          </p>
          <p className="text-sm text-gray-600">
            Total: {pagination.totalRecords}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Pagination</h3>
          <p className="text-sm text-gray-600">
            Page: {pagination.currentPage}/{pagination.totalPages}
          </p>
          <p className="text-sm text-gray-600">
            Limit: {pagination.limit}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Status</h3>
          <p className="text-sm text-gray-600">
            Loading: {isLoading ? 'Yes' : 'No'}
          </p>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateFilterExportTest;

